import { useEffect, useRef, useState } from 'react';
import { JobResult, MiLCALogRecord } from '@typeList/api/system';
import { getAttachedFilesRequestWithHeader, getRequest, postRequest } from '@services/api/api';

const POLLING_INTERVAL_MS = 5000;

const waitForNextPoll = (signal: AbortSignal): Promise<void> => new Promise((resolve, reject) => {
    const onAbort = () => {
        window.clearTimeout(timer);
        reject(new DOMException('EPDジョブの監視を終了しました', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
    }, POLLING_INTERVAL_MS);
    signal.addEventListener('abort', onAbort, { once: true });
});

export const useEpdReportJob = () => {
    const [reportForEpdLoading, setReportForEpdLoading] = useState(false);
    const [reportForEpdLogs, setReportForEpdLogs] = useState<MiLCALogRecord[]>([]);
    const requestController = useRef<AbortController | null>(null);

    useEffect(() => () => requestController.current?.abort(), []);

    const getReportForEpd = async (caseStudyId: number, epdType: string) => {
        if (requestController.current) {
            throw new Error('EPDレポートを作成中です');
        }
        const controller = new AbortController();
        requestController.current = controller;
        const checkActive = () => {
            if (controller.signal.aborted) {
                throw new DOMException('EPDジョブの監視を終了しました', 'AbortError');
            }
        };

        setReportForEpdLoading(true);
        setReportForEpdLogs([]);
        try {
            const job = await postRequest<JobResult>(
                `/case_studies/${caseStudyId}/reports/epd?epd_type=${encodeURIComponent(epdType)}`
            );
            checkActive();
            while (true) {
                const result = await getRequest<JobResult>(`/jobs/${job.job_id}`);
                checkActive();
                setReportForEpdLogs(result.logs);
                const finished = [...result.logs].reverse().find(log => log.status === 'finished');
                if (finished) {
                    if (finished.end_status !== 'success') {
                        throw new Error('EPDレポートの作成に失敗しました');
                    }
                    const file = await getAttachedFilesRequestWithHeader<Blob>(
                        `/case_studies/${caseStudyId}/reports/epd/${job.job_id}/file`
                    );
                    checkActive();
                    return file;
                }
                // 前回の取得完了から待機し、長いリクエスト同士の重複を防ぐ。
                await waitForNextPoll(controller.signal);
                checkActive();
            }
        } finally {
            requestController.current = null;
            if (!controller.signal.aborted) {
                setReportForEpdLoading(false);
            }
        }
    };

    return { getReportForEpd, reportForEpdLoading, reportForEpdLogs };
};
